import ky from 'ky'

import { Store } from '../state'
import { graphql } from './graphql'
import { isExpired } from './session'

// Exports de données brutes (#7688), réservés à l'équipe Nap&Up (comptes
// admin). L'API génère le ZIP en tâche de fond ; le back-office le demande,
// suit son état, puis le télécharge par une route REST authentifiée (le
// fichier ne transite jamais par email).

export type DataExportStatus =
  | 'pending'
  | 'running'
  | 'done'
  | 'failed'
  | 'expired'

export type DataExport = {
  _id: string
  status: DataExportStatus
  startDay: string
  endDay: string
  label: string
  createdAt: string
  finishedAt: string | null
  expiresAt: string | null
  size: number | null
  rows: Record<string, number> | null
  error: string | null
}

const REQUEST_ERRORS: Record<string, string> = {
  invalid_period: 'La période choisie est invalide.',
  period_too_long: 'La période ne peut pas dépasser 24 mois.',
  invalid_space: "Cet espace n'existe pas.",
  unknown_space: "Cet espace n'existe pas.",
  already_running:
    "Un export est déjà en cours. Attendez qu'il se termine pour en lancer un autre.",
  quota_reached: 'Vous avez atteint la limite de 10 exports par 24 heures.',
}

const FAILURE_REASONS: Record<string, string> = {
  interrupted: "Interrompu par un redémarrage du serveur. Relancez l'export.",
  generation_failed: "La génération a échoué. Relancez l'export.",
  forbidden: 'Vos accès ont changé depuis la demande.',
  invalid_period: 'La période choisie est invalide.',
}

export const requestErrorMessage = (code: string) =>
  REQUEST_ERRORS[code] ?? "L'export n'a pas pu être lancé."

export const failureMessage = (code: string | null) =>
  (code && FAILURE_REASONS[code]) ??
  "La génération a échoué. Relancez l'export."

// « 2026-10-31 » à partir des composantes locales de la date choisie dans le
// calendrier : l'API interprète ce jour en heure de Paris.
export const toDay = (date: Date) =>
  [
    date.getFullYear(),
    `${date.getMonth() + 1}`.padStart(2, '0'),
    `${date.getDate()}`.padStart(2, '0'),
  ].join('-')

export const serviceExports = {
  async history() {
    return (
      await graphql.query<{ exportHistory: DataExport[] }>('{ exportHistory }')
    ).exportHistory
  },

  async request(start: Date, end: Date, space: string | null) {
    return (
      await graphql.query<{
        exportRequest: { export?: DataExport; error?: string }
      }>(
        `mutation { exportRequest(start: "${toDay(start)}", end: "${toDay(
          end,
        )}"${space ? `, space: "${space}"` : ''}) }`,
      )
    ).exportRequest
  },

  // Télécharge le ZIP avec le jeton de session, puis le propose à
  // l'enregistrement. Un lien direct ne porterait pas l'en-tête
  // Authorization.
  async download(item: DataExport) {
    const token = Store.getState().utils.session.token

    if (!token || isExpired(token)) {
      Store.getActions().utils.session.disconnect()
      throw new Error('session_expired')
    }

    const url = new URL(
      `exports/${item._id}/download`,
      process.env.REACT_APP_API_URL as string,
    ).toString()

    const blob = await ky
      .get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 120000,
      })
      .blob()

    const href = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = href
    link.download = `napandup-export_${item.startDay}_${item.endDay}.zip`
    document.body.appendChild(link)
    link.click()
    link.remove()
    setTimeout(() => URL.revokeObjectURL(href), 30000)
  },
}
